import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { TextValuePreview } from 'components/detail/edit/TextValuePreview';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { LargeTextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/LargeTextValue';
import { TextValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EHovers } from 'libs/styles/styling';
import { useContext, useState } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    position: number;
    className?: string;
}

export const EditTextCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const [title, setTitle] = useState(card.info.title);
    const [description, setDescription] = useState(card.info.description);
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;

    function changeTitle(value: string): void {
        setTitle(value);
        ctxDetail.updateCardByPosition(
            {
                info: {
                    ...card.info,
                    title: value
                }
            },
            props.position
        );
    }

    function changeDescription(value: string): void {
        setDescription(value);
        ctxDetail.updateCardByPosition(
            {
                info: {
                    ...card.info,
                    description: value
                }
            },
            props.position
        );
    }

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: 'font'
                    }}
                    title={Localization.TEXT}
                    highlighted={!!isUpdatingIndex}
                    topBorder={!isUpdatingIndex}
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content $isUpdatingIndex={isUpdatingIndex}>
                        {title && <S_TextValuePreview value={title} />}
                        {description && <S_TextValuePreview value={description} />}
                        {!title && !description && <S_TextValuePreview value="-" />}
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <>
                        <SidebarSubsection title={Localization.TITLE} icon={{ iconName: 'font' }}>
                            <SidebarContent>
                                <S_TextValue2
                                    value={title}
                                    onChange={changeTitle}
                                    textAlign="left"
                                />
                            </SidebarContent>
                        </SidebarSubsection>
                        <SidebarSubsection
                            title={Localization.DESCRIPTION}
                            icon={{ iconName: 'paragraph' }}
                        >
                            <SidebarContent>
                                <S_LargeTextValue
                                    value={description}
                                    onChange={changeDescription}
                                    maxRows={20}
                                />
                            </SidebarContent>
                        </SidebarSubsection>
                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_TextValuePreview = styled(TextValuePreview)``;

const S_TextValue2 = styled(TextValue)``;

const S_LargeTextValue = styled(LargeTextValue)``;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;
